import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { type Subscription } from 'rxjs';

import { type ElementsState, runtimeActions } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type HasDataProps,
  type RuntimeUiEventPayload,
} from '@blaze-runtime/engine-domain';
import {
  type FormFormulasRefreshTriggerPayload,
  getActiveBinding,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  type DatasetRow,
  type ElementSchema,
  type RuntimeEventType,
} from '@blaze-shared/schema-domain';
import { isSelectOption } from '@blaze-shared/shared-util';
import { isFileResult } from '@blaze-shared/uploader';

import { RuntimeFacade } from '../../../+state/runtime/runtime.facade';
import { FormListRowsService } from '../../../elements/form-list/form-list-rows.service';
import { ElementDefinitionsService } from '../../../services/element-definitions.service';
import { LayoutLockService } from '../../../services/layout-lock.service';
import { SchemaValidationService } from '../../../services/schema-validation.service';
import {
  getFormListDataRowById,
  getFormListIdColumn,
} from '../../../utils/elements/form-list.utils';
import { isFormList, isVariantOfFormContainer } from '../../element-model/element-type-guards';
import {
  type FormGroupLikeRenderElementInstance,
  type FormListRenderElementInstance,
  type FormRenderElementInstance,
  type RenderElementInstance,
} from '../../models/render-element.models';
import {
  type GetChildValueParams,
  type UpdateInputsCacheParams,
} from '../models/children-handler.models';
import { ElementOutputSchemaService } from '../outputs/element-output-schema.service';

import { ChildValueExtractor, type ChildValueResult } from './child-value-extractor';

type FormContainerInstance =
  | FormRenderElementInstance
  | FormListRenderElementInstance
  | FormGroupLikeRenderElementInstance;

interface FormulasRefreshContext {
  formElement: CanvasElement;
  inputElement: CanvasElement;
  formInstance: FormContainerInstance;
  isElementFormList: boolean;
  schema: ElementSchema | null | undefined;
  data: any;
  event: RuntimeUiEventPayload;
}

export abstract class BaseChildrenHandlerService {
  protected readonly childValueExtractor = inject(ChildValueExtractor);
  protected readonly elementDefinitionsService = inject(ElementDefinitionsService);
  protected readonly elementOutputSchemaService = inject(ElementOutputSchemaService);
  protected readonly formListRowsService = inject(FormListRowsService);
  protected readonly layoutLockService = inject(LayoutLockService);
  protected readonly runtimeFacade = inject(RuntimeFacade);
  protected readonly schemaService = inject(SchemaService);
  protected readonly schemaValidationService = inject(SchemaValidationService);
  protected readonly store = inject(Store<ElementsState>);

  private pendingUpdateSubscription: Subscription | undefined;
  private pendingUpdateTimeout: ReturnType<typeof setTimeout> | undefined;

  updateInputsCache(_: UpdateInputsCacheParams): void {
    // implemented in derived classes
  }

  protected getElementOutputSchema(
    element: CanvasElement,
    eventType: RuntimeEventType,
    elementInstance:
      | RenderElementInstance
      | FormRenderElementInstance
      | FormListRenderElementInstance
      | FormGroupLikeRenderElementInstance
      | undefined,
  ): ElementSchema | undefined {
    return this.elementOutputSchemaService.getElementOutputSchema(
      element,
      eventType,
      elementInstance?.getDefaultEventSchema?.(eventType),
    );
  }

  /**
   * Dispatches the parent form container's output after a child field changes.
   *
   * Uses layoutLockService.unlocked$ + setTimeout to wait until all children
   * are created and initialized before emitting, preventing partial object emissions.
   */
  protected updateDataContainerOutput(
    formElement: CanvasElement,
    inputElement: CanvasElement,
    formInstance: FormContainerInstance,
    event: RuntimeUiEventPayload,
    triggerFormulasRefresh?: boolean,
  ): void {
    this.cancelPendingUpdate();

    this.pendingUpdateSubscription = this.layoutLockService.unlocked$.subscribe(() => {
      this.pendingUpdateTimeout = setTimeout(() => {
        if (!formInstance.shouldEmitOutput()) {
          return;
        }

        const schema =
          formInstance.schema || this.getElementOutputSchema(formElement, 'change', formInstance);
        const data = formInstance.getInstanceValue();
        const payload: RuntimeUiEventPayload = {
          data,
          elementId: formElement.id,
          eventType: 'change',
          isInitialData: event.isInitialData ?? false,
          ns: formElement.ns,
          schema,
          updateSource: `form-input:${inputElement.id}`,
          rowContext: event.rowContext,
          eventContext: event.eventContext,
        };

        this.schemaValidationService.validateElementOutputEvent(formElement, {
          ...payload,
          schema: formInstance.schema,
        });

        this.store.dispatch(runtimeActions.runtimeUiEvent(payload));

        const isElementFormList = isFormList(formElement);
        if (
          (isVariantOfFormContainer(formElement) || isElementFormList) &&
          triggerFormulasRefresh &&
          formInstance.elementId &&
          inputElement?.id
        ) {
          this.dispatchFormulasRefresh({
            formElement,
            inputElement,
            formInstance,
            isElementFormList,
            schema,
            data,
            event: payload,
          });
        }
      });
    });
  }

  /**
   * Resolves the field name that should be extracted from event data
   * when updating a form container field value.
   */
  protected getDataValueField(
    inputElement: CanvasElement & HasDataProps,
    eventData: any,
  ): string | boolean {
    const optionsListInput = this.elementDefinitionsService.getInputConfig(
      inputElement.type,
      'optionsList',
    );
    const optionsListBinding = getActiveBinding(inputElement.props.data, 'optionsList');
    if (optionsListInput && optionsListBinding) {
      return this.schemaService.getColumnName(
        optionsListBinding.bindings?.['value'] ?? '',
      ) as string;
    }

    const isArrayOf = (pred: (x: unknown) => boolean): boolean =>
      Array.isArray(eventData) && eventData.length > 0 && eventData.every(pred);

    if (isSelectOption(eventData) || isArrayOf(isSelectOption)) {
      return 'value';
    }
    if (isFileResult(eventData) || isArrayOf(isFileResult)) {
      return 'url';
    }

    return true;
  }

  /**
   * Extracts and coerces a child element's event data into the value expected
   * by the parent form container.
   */
  protected getChildValue(params: GetChildValueParams): ChildValueResult {
    return this.childValueExtractor.extract(params);
  }

  private cancelPendingUpdate(): void {
    clearTimeout(this.pendingUpdateTimeout);
    this.pendingUpdateSubscription?.unsubscribe();
  }

  private dispatchFormulasRefresh({
    formElement,
    inputElement,
    isElementFormList,
    schema,
    data,
    event,
  }: FormulasRefreshContext): void {
    let dataRow = data as DatasetRow | undefined;
    let columns = schema?.columns;

    if (isElementFormList) {
      const { rowId } = event.rowContext ?? {};
      if (rowId === undefined) {
        throw new Error('formula-refresh: missing rowId');
      }
      const idColumn = getFormListIdColumn(schema ?? undefined);
      if (!idColumn) {
        console.error('formula-refresh: missing idColumn', schema);
        return;
      }
      const emptyishRows = this.formListRowsService.getFormListEmptyishRows(formElement.id);
      dataRow = getFormListDataRowById(
        data as DatasetRow[],
        emptyishRows,
        idColumn,
        rowId,
      )?.dataRow;
      columns = schema?.columns[0]?.dataTypeDef?.columns;
    }

    if (!dataRow) {
      return;
    }

    const refreshPayload: FormFormulasRefreshTriggerPayload = {
      formElementId: formElement.id,
      inputElementId: inputElement.id,
      columns,
      dataRow,
      datasetId: schema?.datasetId,
      rowContext: event.rowContext,
    };
    this.store.dispatch(runtimeActions.formFormulasRefreshTrigger(refreshPayload));
  }
}
